import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSession } from '@/hooks/useSession';
import {
  deleteQariDocument,
  getMyQariProfile,
  listMyQariDocuments,
  submitQariProfileForReview,
  upsertQariDraft,
  uploadQariDocument,
  withdrawQariSubmission,
} from '@/lib/queries/qari';
import type {
  QariDocument,
  QariDocumentKind,
  QariProfile,
  QariProfileDraft,
} from '@/types/qari';

/**
 * useQariProfile — the caller's own qari_profile row.
 *
 * Returns `null` if the user is signed-in but has not started a
 * profile yet (no row exists). Returns `undefined` while still loading
 * or if the user is signed-out.
 *
 * Refetch policy: this query is deliberately quiet. It fetches once on
 * mount, doesn't revalidate on window focus, and is updated in place
 * via `queryClient.setQueryData` by the mutations below — so a save
 * does NOT trigger a refetch. The user can force a refetch by
 * reloading the page or by calling `.refetch()` explicitly.
 */
export function useQariProfile() {
  const session = useSession();
  const userId = session.data?.user.id ?? null;

  return useQuery({
    queryKey: ['qari-profile', userId],
    queryFn: () => {
      if (!userId) throw new Error('Sign in required.');
      return getMyQariProfile(userId);
    },
    enabled: !!userId,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  });
}

/**
 * useQariDocuments — the caller's own uploaded verification docs.
 *
 * The document list mutates rarely (one row per upload/delete) and is
 * a separate concern from the typing buffer, so it CAN invalidate on
 * change without disrupting the user's editing.
 */
export function useQariDocuments() {
  const session = useSession();
  const userId = session.data?.user.id ?? null;

  return useQuery<QariDocument[]>({
    queryKey: ['qari-documents', userId],
    queryFn: () => {
      if (!userId) throw new Error('Sign in required.');
      return listMyQariDocuments(userId);
    },
    enabled: !!userId,
    staleTime: 30_000,
    retry: 1,
  });
}

/**
 * Mutations co-located so the onboarding flow can wire up save-draft,
 * submit, withdraw, and document upload without 5 separate imports.
 *
 * Every profile-row mutation here uses `setQueryData` instead of
 * `invalidateQueries`, so the cache stays in sync with the server WITHOUT
 * triggering a refetch — keystroke-driven autosave (via
 * `useDebouncedAutosave`) stays smooth and inputs never remount mid-typing.
 */
export function useQariMutations() {
  const qc = useQueryClient();
  const session = useSession();
  const userId = session.data?.user.id ?? null;

  const writeProfileToCache = (saved: QariProfile) => {
    qc.setQueryData(['qari-profile', userId], saved);
  };

  const invalidateDocs = () =>
    qc.invalidateQueries({ queryKey: ['qari-documents', userId] });

  const saveDraft = useMutation({
    mutationFn: async (patch: Partial<QariProfileDraft>) => {
      if (!userId) throw new Error('Sign in required.');
      return upsertQariDraft(userId, patch);
    },
    onSuccess: writeProfileToCache,
  });

  const submitForReview = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error('Sign in required.');
      return submitQariProfileForReview(userId);
    },
    onSuccess: writeProfileToCache,
  });

  const withdrawSubmission = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error('Sign in required.');
      return withdrawQariSubmission(userId);
    },
    onSuccess: writeProfileToCache,
  });

  const uploadDocument = useMutation({
    mutationFn: async (input: {
      kind: QariDocumentKind;
      file: File;
      notes?: string | null;
    }) => {
      if (!userId) throw new Error('Sign in required.');
      return uploadQariDocument({ userId, ...input });
    },
    onSuccess: invalidateDocs,
  });

  const deleteDocument = useMutation({
    mutationFn: async (doc: QariDocument) => deleteQariDocument(doc),
    onSuccess: invalidateDocs,
  });

  return {
    saveDraft,
    submitForReview,
    withdrawSubmission,
    uploadDocument,
    deleteDocument,
  };
}
